import { IChoiceGroupOption, IDropdownOption } from "@fluentui/react";
import { Contract } from "../../../../models/Entities/Contracts/Contract";
import { ContractConcept } from "../../../../models/Entities/Contracts/ContractConcept";
import { ContractStage } from "../../../../models/Entities/Contracts/ContractStage";
import { ErrorType } from "../../../../models/HttpError";

// stores
import * as ContractConceptStore from '../../../../redux/middleware/ContractConceptMiddleware';
import * as ContractStore from '../../../../redux/middleware/ContractMiddleware';
import * as ContractConceptPaymentScheduleStore from '../../../../redux/middleware/ContractConceptPaymentScheduleMiddleware';
import * as ContractConceptConceptStore from '../../../../redux/middleware/ContractConceptConceptMiddleware';
import * as ContractAddendumStore from '../../../../redux/middleware/ContractAddendum.Middleware';

//State
import { AppState } from "../../../../redux/reducers";
import { ContractConceptConcept } from "../../../../models/Entities/Contracts/ContractConceptConcept";


interface IProps {
  isOpen: boolean;
  openPanel: () => void;
  dismissPanel: () => void;
  ContractId: number;
  ContractAddendumId: number;
  ContractConceptId: number | undefined;
  CurrentConcepts: ContractConcept[];
  CurrentStages: ContractStage[];
}

interface ConnectedProps {
  isContractLoaded: boolean;
  isConceptLoaded: boolean;
  ConceptsLoaded: boolean;
  isAdding: boolean;
  isAdded: boolean;
  isUpdated: boolean;
  isUpdating: boolean;
  failOnAdding: boolean;
  failOnUpdating: boolean;
  error: ErrorType | undefined;
  Contract: Contract;
  Concept: ContractConcept;
  AddendumLoaded: boolean;
  Concepts: ContractConcept[] | undefined;
}

interface DispatchProps {
  GetContractConceptById: typeof ContractConceptStore.actionCreators.GetContractConceptById;
  AddContractConcept: typeof ContractConceptStore.actionCreators.AddContractConcept;
  UpdateContractConcept: typeof ContractConceptStore.actionCreators.UpdateContractConcept;
  SetContractConceptApproved: typeof ContractConceptStore.actionCreators.SetContractConceptApproved;
  GetContractAddendumsByContractId: typeof ContractAddendumStore.actionCreators.GetContractAddendumsByContractId;
  GetAllContractsConceptByAddendum: typeof ContractConceptStore.actionCreators.GetAllContractsConceptByAddendum;
  GetContractById: typeof ContractStore.actionCreators.GetContractById;
  GetAllContractsConcept: typeof ContractConceptStore.actionCreators.GetAllContractsConcept;
  GetAllContractsConceptPaymentSchedule: typeof ContractConceptPaymentScheduleStore.actionCreators.GetAllContractsConceptPaymentSchedule;
  GetAllContractConceptConcepts: typeof ContractConceptConceptStore.actionCreators.GetAllContractConceptConcepts;
  AddContractConceptConcept: typeof ContractConceptConceptStore.actionCreators.AddContractConceptConcept;
  UpdateContractConceptConcept: typeof ContractConceptConceptStore.actionCreators.UpdateContractConceptConcept;
  AddRangeContractConceptConcepts: typeof ContractConceptConceptStore.actionCreators.AddRangeContractConceptConcepts;
  AddContractConceptPaymenSchedule: typeof ContractConceptPaymentScheduleStore.actionCreators.AddContractConceptPaymenSchedule;
  DeleteContractConceptConcept: typeof ContractConceptConceptStore.actionCreators.DeleteContractConceptConcept;
  DeleteContractConceptPaymentSchedule: typeof ContractConceptPaymentScheduleStore.actionCreators.DeleteContractConceptPaymentSchedule;
}

export type Props = IProps & ConnectedProps & DispatchProps;

/*
 * mapStateToProps runs on every dispatch, and rebuilding the concepts array here
 * gave the form a new prop identity each time, re-rendering it - and re-seeding
 * the concept selector - on completely unrelated store activity. Every reducer
 * that touches a concept replaces the addendum concepts array, so its identity
 * plus the signature of the current selection is enough to reuse the last result.
 */
interface ConceptsMemoEntry {
  source: ContractConcept[];
  selectionKey: string;
  value: ContractConcept[];
}

const conceptsMemo = new Map<string, ConceptsMemoEntry>();

const buildConcepts = (
  memoKey: string,
  addendumConcepts: ContractConcept[] | undefined,
  contractConceptId: number | undefined
): ContractConcept[] | undefined => {
  if (!addendumConcepts) return undefined;

  const selectedIds = (addendumConcepts.find((concept: ContractConcept) => concept.id === contractConceptId)?.concepts || [])
    .map((conceptConcept: ContractConceptConcept) => conceptConcept.relatedContractConceptId);
  const selectionKey = [...selectedIds].sort((first: number, second: number) => first - second).join(',');

  const cached = conceptsMemo.get(memoKey);
  if (cached && cached.source === addendumConcepts && cached.selectionKey === selectionKey) {
    return cached.value;
  }

  const value = addendumConcepts.map((concept: ContractConcept) => {
    return { ...concept, isSelected: selectedIds.includes(concept.id), isUnselected: false };
  });

  /* One entry per open form; drop everything if too many forms went through. */
  if (conceptsMemo.size > 50) conceptsMemo.clear();
  conceptsMemo.set(memoKey, { source: addendumConcepts, selectionKey, value });

  return value;
};

export const mapStateToProps = (state: AppState, ownProps: IProps) => {

  const CurrentContract = state.Contract?.list?.find((item: Contract) => item.id === ownProps.ContractId ) || undefined;
  const CurrentAddendum = CurrentContract?.addendums?.find((item: any) => item.id === ownProps.ContractAddendumId) || undefined;
  const Concept = CurrentAddendum?.concepts?.find((item: any) => item.id === ownProps.ContractConceptId) || undefined;


  return{
    isContractLoaded: state.Contract?.successLoadingOne,
    isConceptLoaded: state.ContractConcept?.successLoadingOne,
    ConceptsLoaded: state.ContractConceptConcept?.successLoadingAll,
    isAdded: state.ContractConcept?.successAddingNewOne,
    isAdding: state.ContractConcept?.isAddingNewOne,
    isUpdated: state.ContractConcept?.successUpdatingOne,
    isUpdating: state.ContractConcept?.isUpdatingOne,
    failOnAdding: state.ContractConcept?.failOnAddingNewOne,
    failOnUpdating: state.ContractConcept?.failOnUpdatingOne,
    error: state.ContractConcept?.error,
    Contract: CurrentContract,
    /* The addendum has to exist in the tree before its concepts can be attached. */
    AddendumLoaded: CurrentAddendum !== undefined,
    Concept: Concept,
    Concepts: buildConcepts(
      `${ownProps.ContractId}|${ownProps.ContractAddendumId}|${ownProps.ContractConceptId}`,
      CurrentAddendum?.concepts,
      ownProps.ContractConceptId
    )
  }
};

export const mapDispatchToProps = {
  ...ContractConceptStore.actionCreators,
  ...ContractStore.actionCreators,
  ...ContractConceptPaymentScheduleStore.actionCreators,
  ...ContractConceptConceptStore.actionCreators,
  ...ContractAddendumStore.actionCreators
};

export interface IValueOriginOption extends IDropdownOption {
  isFixedPrice?: boolean;
  isCostValue?: boolean;
  isPercentage?: boolean;
  isPriceList?: boolean;
  isDefinedByVisit?: boolean;
}

export interface ISettlementOption extends IDropdownOption {
  isIncludeInFirstSettlement?: boolean;
  isIncludeInLastSettlement?: boolean;
  isIncludeInEverySettlement?: boolean;
  isIdentifyVisit?: boolean;
  isIncludeInEveryStage?: boolean;
  isByPeriod?: boolean;
  isByPatient?: boolean;
  isByAddendum?: boolean;
  isManual?: boolean;
  isByConcept?: boolean;
}


export const SettlementOptions: ISettlementOption[] = [
  { key: '1', text: 'SettlementOptions.FirstSettlement', 
    isIncludeInFirstSettlement: true, 
    isIncludeInLastSettlement: false, 
    isIncludeInEverySettlement: false, 
    isIdentifyVisit: false, 
    isByPeriod: false, 
    isIncludeInEveryStage: false,
    isByPatient: false,
    isByAddendum: false,
    isManual: false,
    isByConcept: false
  },
  { key: '2', text: 'SettlementOptions.LastSettlement', 
    isIncludeInFirstSettlement: false, 
    isIncludeInLastSettlement: true, 
    isIncludeInEverySettlement: false, 
    isIdentifyVisit: false, 
    isByPeriod: false, 
    isIncludeInEveryStage: false,
    isByPatient: false,
    isByAddendum: false,
    isManual: false,
    isByConcept: false
  },
  { key: '3', text: 'SettlementOptions.EachSettlement', 
    isIncludeInFirstSettlement: false, 
    isIncludeInLastSettlement: false, 
    isIncludeInEverySettlement: true, 
    isIdentifyVisit: false, 
    isByPeriod: false, 
    isIncludeInEveryStage: false,
    isByPatient: false,
    isByAddendum: false,
    isManual: false,
    isByConcept: false
  },
  { key: '4', text: 'SettlementOptions.ByStage', 
    isIncludeInFirstSettlement: false, 
    isIncludeInLastSettlement: false, 
    isIncludeInEverySettlement: false, 
    isIdentifyVisit: true, 
    isByPeriod: false, 
    isIncludeInEveryStage: false,
    isByPatient: false,
    isByAddendum: false,
    isManual: false,
    isByConcept: false
  },
  { key: '6', text: 'SettlementOptions.AllStage', 
    isIncludeInFirstSettlement: false, 
    isIncludeInLastSettlement: false, 
    isIncludeInEverySettlement: false, 
    isIdentifyVisit: false, 
    isByPeriod: false, 
    isIncludeInEveryStage: true,
    isByPatient: false,
    isByAddendum: false,
    isManual: false,
    isByConcept: false
  },
  { key: '5', text: 'SettlementOptions.ByPeriod', 
    isIncludeInFirstSettlement: false, 
    isIncludeInLastSettlement: false, 
    isIncludeInEverySettlement: false, 
    isIdentifyVisit: false, 
    isByPeriod: true, 
    isIncludeInEveryStage: false,
    isByPatient: false,
    isByAddendum: false,
    isManual: false,
    isByConcept: false
  },
  { key: '7', text: 'SettlementOptions.ByPatient', 
    isIncludeInFirstSettlement: false, 
    isIncludeInLastSettlement: false, 
    isIncludeInEverySettlement: true, 
    isIdentifyVisit: false, 
    isByPeriod: false, 
    isIncludeInEveryStage: true,
    isByPatient: true,
    isByAddendum: false,
    isManual: false,
    isByConcept: false
  },
  { key: '8', text: 'SettlementOptions.Manual', 
    isIncludeInFirstSettlement: false, 
    isIncludeInLastSettlement: false, 
    isIncludeInEverySettlement: true, 
    isIdentifyVisit: false, 
    isByPeriod: true, 
    isIncludeInEveryStage: false,
    isByPatient: false,
    isByAddendum: false,
    isManual: true,
    isByConcept: false
  },
  { key: '9', text: 'SettlementOptions.ByAddendum', 
    isIncludeInFirstSettlement: false, 
    isIncludeInLastSettlement: false, 
    isIncludeInEverySettlement: true, 
    isIdentifyVisit: false, 
    isByPeriod: false, 
    isIncludeInEveryStage: false,
    isByPatient: false,
    isByAddendum: true,
    isManual: false,
    isByConcept: false
  },
  { key: '10', text: 'SettlementOptions.ByConcept', 
    isIncludeInFirstSettlement: false, 
    isIncludeInLastSettlement: false,
    isIncludeInEverySettlement: true,
    isIdentifyVisit: false,
    isByPeriod: false,
    isIncludeInEveryStage: false,
    isByPatient: false,
    isByAddendum: false,
    isManual: false,
    isByConcept: true
  }
];

export const PeriodOptions: IDropdownOption[] = [
  { key: '1', text: 'PeriodOptions.Monthly' },
  { key: '2', text: 'PeriodOptions.Bimonthly' },
  { key: '3', text: 'PeriodOptions.Quarterly' },
  { key: '6', text: 'PeriodOptions.FourMonthly' },
  { key: '4', text: 'PeriodOptions.SemiAnnual' },
  { key: '5', text: 'PeriodOptions.Annual' }
];

export const UnitMeasurementOptions: IDropdownOption[] = [
  { key: '1', text: 'UnitMeasurementOptions.ByConcept' },
  { key: '2', text: 'UnitMeasurementOptions.ByHour' }
];

export const ValueOriginOptions: IValueOriginOption[] = [
  { key: '1', text: 'ValueOriginOptions.FixedValue', isFixedPrice: true, isPercentage: false, isPriceList: false, isDefinedByVisit: false, isCostValue: false },
  { key: '2', text: 'ValueOriginOptions.ValueByCost', isFixedPrice: false, isPercentage: false, isPriceList: false, isDefinedByVisit: false, isCostValue: true },
  { key: '3', text: 'ValueOriginOptions.ValueByPercentage', isFixedPrice: false, isPercentage: true, isPriceList: false, isDefinedByVisit: false, isCostValue: false },
  { key: '4', text: 'ValueOriginOptions.ValueByPriceList', isFixedPrice: false, isPercentage: false, isPriceList: true, isDefinedByVisit: false, isCostValue: false },
  { key: '5', text: 'ValueOriginOptions.ValueByStage', isFixedPrice: false, isPercentage: false, isPriceList: false, isDefinedByVisit: true, isCostValue: false }
];

export const PriceListOptions: IDropdownOption[] = [
  { key: '1', text: 'Lista de precio propia' },
  { key: '2', text: 'Lista de precio proveedor' },
  { key: '2', text: 'Lista de precio cobertura médica' }
];

export interface IPercentageOriginOption extends IChoiceGroupOption {
  takeFromConcepts?: boolean;
  takeFromPriceList?: boolean;
}

export const PercentageOriginOptions: IPercentageOriginOption[] = [
  { key: '1', text: 'En base a conceptos', takeFromConcepts: true, takeFromPriceList: false },
  { key: '2', text: 'En base a lista de precios', takeFromConcepts: false, takeFromPriceList: true }
];

export interface ICostOptions extends IDropdownOption {
  isFixedPrice?: boolean;
  isPercentage?: boolean;
  isProviderValue?: boolean;
  isPriceList?: boolean;
  isTakeFromServices?: boolean;
}

export const ConceptCostOptions: ICostOptions[] = [
  { key: '1', text: 'Valor fijo', isFixedPrice: true, isPercentage: false, isProviderValue: false, isPriceList: false, isTakeFromServices: false },
  { key: '2', text: 'Valor Porcentaje del precio fijo', isFixedPrice: false, isPercentage: true, isProviderValue: false, isPriceList: false, isTakeFromServices: false },
  { key: '3', text: 'Valor lista de precio', isFixedPrice: false, isPercentage: false, isProviderValue: false, isPriceList: true, isTakeFromServices: false },
  { key: '4', text: 'Tomar de servicios', isFixedPrice: false, isPercentage: false, isProviderValue: false, isPriceList: false, isTakeFromServices: true },
];

export const OriginConceptCostOptions: IDropdownOption[] = [
  { key: '1', text: 'Lista de precios propia' },
  { key: '2', text: 'Lista de precios de proveedor' },
];

export const calculateTypeOptions: IDropdownOption[] = [
  { key: '1', text: 'CalculateTypeOptions.ByUnit' },
  { key: '2', text: 'CalculateTypeOptions.ByShift' },
  { key: '3', text: 'CalculateTypeOptions.ByVisit' },
  { key: '4', text: 'CalculateTypeOptions.ByHour' }
];

export const getCalculateTypeDescription = (calculateType: number | undefined): string => {
  switch (calculateType) {
    case (1): return 'CalculateTypeDescription.Units';
    case (2): return 'CalculateTypeDescription.Shifts';
    case (3): return 'CalculateTypeDescription.Visits';
    case (4): return 'CalculateTypeDescription.Hours';
    default: return '';
  }
}

export const normalizeIncludedInContractConceptId = (value: number | null | undefined): number => {
  if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value) || value < 0) {
    return 0;
  }

  return Math.trunc(value);
}

export const getContractConceptFieldDefaults = (concept: Partial<ContractConcept> | null | undefined) => {
  return {
    allowInputByMedicalSector: concept?.allowInputByMedicalSector ?? true,
    incluedInContractConceptId: normalizeIncludedInContractConceptId(concept?.incluedInContractConceptId),
    markIsCostOfConcepyt: concept?.markIsCostOfConcepyt ?? true
  };
}
